<script lang="ts">
  import { waveformChoiceLabel } from '../lib/event-waveform-series';
  import type { AnalysisState } from '../lib/types';
  import type { AnalysisRow } from './analysis-panel-types';

  export let rows: AnalysisRow[];
  export let state: AnalysisState;
  export let maximumSelectedPlots: number;
  export let modeDescription: string;
  export let selectionLabel: string;
  export let onTogglePlot: (plotId: string, checked: boolean) => void;
  export let onChooseSeries: (plotId: string, seriesId: string) => void;
  export let onChooseMode: (mode: string) => void;
  export let onSetWindow: (value: string) => void;
</script>

<fieldset class="analysis-selection" aria-describedby="analysis-selection-help">
  <legend>Plot panels</legend>
  <p id="analysis-selection-help" class="analysis-help">Select one plot for descriptive statistics or two or more for comparison. Choose up to four plots.</p>
  <div class="analysis-selection__grid">
    {#each rows as row (row.plot.id)}
      <div class:analysis-source--selected={row.selected} class="analysis-source">
        <label class="analysis-source__toggle">
          <span class="analysis-source__choice">
            <input
              type="checkbox"
              aria-label={row.title}
              checked={row.selected}
              disabled={!row.selected && (
                state.selectedPlotIds.length >= maximumSelectedPlots
                || Boolean(row.source && row.series.length === 0)
                || (state.dataMode === 'event-waveforms' && !row.plot.selectedEvent)
              )}
              on:change={(event) => onTogglePlot(row.plot.id, (event.currentTarget as HTMLInputElement).checked)}
            />
            <span>{row.title}</span>
          </span>
          <span class="analysis-source__status">
            {#if state.dataMode === 'event-waveforms'}
              {#if !row.plot.selectedEvent}
                Select an event in this plot first
              {:else if row.source}
                {row.choices.length ? `${row.source.monitorName} - ${row.source.model.subtitle ?? 'Captured event'}` : state.mode === 'calculated-power' ? 'No matching voltage/current phase pair is available' : state.mode === 'three-phase-balance' ? 'Three matching phase channels are not available' : row.source.model.emptyMessage ?? 'No captured waveform samples are available'}
              {:else if row.selected}
                Loading captured event waveform…
              {:else}
                Select to load its captured event waveform
              {/if}
            {:else if row.source}
              {row.series.length ? row.source.monitorName : 'This plot has no comparable line series'}
            {:else if row.selected}
              Loading plotted data…
            {:else}
              Select to load its plotted data
            {/if}
            {#if state.dataMode === 'event-waveforms' && row.source?.eventClassification}
              <span class="analysis-source__classification">
                · Classification: <strong>{row.source.eventClassification}</strong>
              </span>
            {/if}
          </span>
        </label>
        {#if row.selected && row.choices.length > 1}
          <label class="analysis-source__series">{selectionLabel}
            <select
              aria-label={`${selectionLabel} for Plot ${row.index + 1}`}
              value={row.selectedSeries?.id}
              on:change={(event) => onChooseSeries(row.plot.id, (event.currentTarget as HTMLSelectElement).value)}
            >
              {#each row.choices as series (series.id)}<option value={series.id}>{waveformChoiceLabel(state.mode, series)}</option>{/each}
            </select>
          </label>
        {/if}
      </div>
    {/each}
  </div>
</fieldset>

<div class="analysis-controls">
  <label>Analysis view
    <select value={state.mode} on:change={(event) => onChooseMode((event.currentTarget as HTMLSelectElement).value)}>
      <option value="raw-comparison">Original values</option>
      <option value="normalized-comparison">Normalized comparison</option>
      <option value="difference-from-baseline">Difference from first series</option>
      <option value="rolling-correlation">Rolling correlation</option>
      {#if state.dataMode === 'event-waveforms'}
        <option value="rms-peak-envelope">RMS and peak envelope</option>
        <option value="frequency-spectrum">Frequency spectrum</option>
        <option value="calculated-power">Calculated power</option>
        <option value="three-phase-balance">Three-phase balance</option>
        <option value="time-frequency">Time-frequency view</option>
        <option value="event-ensemble">Event ensemble</option>
      {/if}
    </select>
  </label>
  <p class="analysis-view-description">{modeDescription}</p>
  {#if state.mode === 'rolling-correlation'}
    <label>Window (matched samples)
      <input type="number" min="3" max="500" step="1" value={state.windowSize} on:change={(event) => onSetWindow((event.currentTarget as HTMLInputElement).value)} />
    </label>
  {/if}
</div>
